/**
 * Fractional indexing is a way of indexing items such that you can maintain the ordering
 * in the face of possibly limitless insertions, updates, deletions, etc.
 *
 * You can think of a fractional index as an index between 0 and 1, which allows fractional
 * parts (rational numbers). Because you can infinitely subdivide spaces between fractions,
 * you will always have "room to do an insertion".
 *
 * In a concurrent world, we need to add a bit of random noise when a fractional index is
 * generated, otherwise two users or a user at different times might generate the same
 * fractional index, which causes undefined behavior.
 *
 * In our implementation, a fractional index looks exactly like a regular fraction, e.g.
 * 0.0001, 0.123, 0.55555, etc. However, we remove the first two digits to save space, and
 * the boundaries 0 and 1 are invalid. So the above examples would be 0001, 123, 55555.
 *
 * The fractional indexes are stored as strings, even though the strings are just numbers.
 * This allows us to have infinite precision.
 *
 * We implement the functions:
 *
 * generateFractionalIndex(seed: number)
 * generateNIndicesBetween(index: string, min?: string, max?: string)
 */

/**
 * Helper function to compare two fractional index strings
 * Returns -1 if a < b, 0 if a === b, 1 if a > b
 */
export function compareFractionalIndices(a: string, b: string) {
  // Pad with zeros to make same length
  const maxLength = Math.max(a.length, b.length)
  const aPadded = a.padEnd(maxLength, "0")
  const bPadded = b.padEnd(maxLength, "0")

  if (aPadded === bPadded)
    return 0
  return aPadded < bPadded ? -1 : 1
}

/**
 * Helper function to compare two fractional index strings
 * Returns -1 if a < b, 0 if a === b, 1 if a > b
 */
function trimTrailingZeros(a: string) {
  for (const i of range(a.length).reverse())
    if (a[i] !== "0") {
      return a.slice(0, i + 1)
    }
  return a
}

/**
 * Helper function to generate a random digit string of specified length
 */
function generateRandomDigits(length: number) {
  let result = ""
  for (let i = 0; i < length; i++) {
    result += Math.floor(Math.random() * 10).toString()
  }
  return trimTrailingZeros(result) || "0"
}

/**
 * Helper function to find a string halfway between two strings,
 * maintaining string-based decimal precision
 */
function midpoint(a?: string, b?: string) {
  // If either input is empty/undefined, use boundary values. The upper bounds need only be
  // the length of the lower bound
  if (!a)
    a = "0"
  if (!b)
    b = Array.from({ length: a.length + 1 }).fill(9).join("")

  // Pad to equal length
  const maxLength = Math.max(a.length, b.length)
  const aPadded = a.padEnd(maxLength, "0")
  const bPadded = b.padEnd(maxLength, "0")

  // Ensure a < b
  if (compareFractionalIndices(aPadded, bPadded) >= 0) {
    throw new Error("First argument must be less than second argument")
  }

  const noise = Math.floor(Math.random() * 100).toString()

  // Find the first differing digit.
  let i = 0
  while (i < maxLength && (aPadded[i]) === (bPadded[i]))
    i++

  // Get the differing digits
  const aDigit = Number.parseInt(aPadded[i] || "0")
  const bDigit = Number.parseInt(bPadded[i] || "0")
  if (Number.isNaN(aDigit) || Number.isNaN(bDigit))
    throw new Error(`Non-digit inputs: ${aPadded} ${bPadded}`)
  if (aDigit === bDigit)
    throw new Error("a digit and b digit are the same?")

  // If the digits are consecutive, we need to keep incrementing a until we hit a non-9, then
  // add random noise to that
  if (aDigit + 1 === bDigit) {
    do { i++ } while (i < maxLength && (aPadded[i] === "9"))

    const lastDigit = Number.parseInt(aPadded[i] || "0")
    const prefix = aPadded.substring(0, i)
    const midDigit = Math.floor((lastDigit + 10) / 2)
    const output = prefix + midDigit + noise
    return output
  }

  // Get the max shared prefix up to the differing digit and take the average
  const prefix = aPadded.substring(0, i).padEnd(i - 1, "0")
  const midDigit = Math.floor((aDigit + bDigit) / 2)
  if (midDigit === aDigit || midDigit === bDigit)
    throw new Error("Mid digit should never equal a or b")
  const output = prefix + midDigit + noise
  return output
}

/**
 * Generates a fractional index with some random noise based on a seed number.
 * The result will be a string representing a number between 0 and 1 (exclusive)
 * with the leading "0." removed.
 *
 * @returns {string} A string representing the fractional index
 */
export function generateFractionalIndex() {
  // Add some random noise to avoid collisions
  return generateRandomDigits(10)
}

/**
 * Generates a fractional index that falls between two other indices.
 * If min is not provided, assumes we're generating an index before max.
 * If max is not provided, assumes we're generating an index after min.
 *
 * @param {string} [min] - The minimum bound (exclusive)
 * @param {string} [max] - The maximum bound (exclusive)
 * @returns {string} A new fractional index that falls between min and max
 */
export function generateIndexBetween(min?: string, max?: string) {
  // Handle cases where min or max are not provided
  if (!min && !max) {
    // Generate somewhere in the middle of the range
    return trimTrailingZeros(`5${generateRandomDigits(4)}`)
  }

  if (!min) {
    return trimTrailingZeros(midpoint(undefined, max))
  }

  if (!max) {
    return trimTrailingZeros(midpoint(min))
  }

  // Both min and max provided
  if (compareFractionalIndices(min, max) >= 0) {
    throw new Error(`Min must be less than max: ${min} ${max}`)
  }

  return trimTrailingZeros(midpoint(min, max))
}

export function generateIndicesBetween(count: number, min?: string, max?: string) {
  if (count < 1)
    throw new Error("Must have 1 or more indices")
  const queue: [string | undefined, string | undefined][] = [[min, max]]
  while (len(queue) < count + 1) {
    const [min, max] = queue.pop()!
    let between = generateIndexBetween(min, max)
    queue.unshift([min, between], [between, max])
  }
  const results = queue.map(([_, n]) => n!).filter(n => isDefined(n)).sort((a, b) => compareFractionalIndices(a, b)).slice(0, count)
  if (results.length !== count)
    throw new Error("Invalid operation")
  return results
}
